import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_type: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePropertyTypeChange = (value) => {
    setFormData({
      ...formData,
      property_type: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/submit-lead`, formData);
      
      if (response.status === 200) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          property_type: '',
          message: ''
        });
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setStatus('idle');
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Gagal mengirim formulir. Silakan coba lagi.');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 px-6 lg:px-12 bg-isk-bg" data-testid="contact-section">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-isk-text tracking-tight mb-4">
            Mulai Kerja Sama
          </h2>
          <p className="text-lg text-isk-muted max-w-2xl mx-auto">
            Isi 3 data singkat untuk memulai diskusi awal dengan tim ISK mengenai potensi pengelolaan properti Anda.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="glass-card rounded-2xl p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
            <div className="rounded-xl bg-isk-teal/5 border border-isk-teal/10 px-4 py-3">
              <div className="text-sm font-semibold text-isk-text">Respon cepat</div>
              <div className="text-sm text-isk-muted">Kami hubungi dalam 1x24 jam.</div>
            </div>
            <div className="rounded-xl bg-isk-teal/5 border border-isk-teal/10 px-4 py-3">
              <div className="text-sm font-semibold text-isk-text">Tanpa komitmen awal</div>
              <div className="text-sm text-isk-muted">Cocok untuk eksplorasi kebutuhan Anda dulu.</div>
            </div>
            <div className="rounded-xl bg-isk-teal/5 border border-isk-teal/10 px-4 py-3">
              <div className="text-sm font-semibold text-isk-text">Fokus properti</div>
              <div className="text-sm text-isk-muted">Kami review skema yang paling relevan.</div>
            </div>
          </div>

          {status === 'success' && (
            <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 flex items-start" data-testid="success-message">
              <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-green-900 mb-1">Terima kasih!</h4>
                <p className="text-sm text-green-700">
                  Data Anda sudah kami terima. Tim ISK akan menghubungi Anda dalam 1x24 jam.
                </p>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start" data-testid="error-message">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-red-900 mb-1">Terjadi kesalahan</h4>
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-isk-text font-semibold">
                  Nama Lengkap *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Masukkan nama lengkap"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="input-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-isk-text font-semibold">
                  Nomor WhatsApp *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="08xx-xxxx-xxxx"
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="input-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="property_type" className="text-isk-text font-semibold">
                Jenis Properti *
              </Label>
              <Select 
                value={formData.property_type} 
                onValueChange={handlePropertyTypeChange}
                required
              >
                <SelectTrigger 
                  className="glass-input border-2 focus:border-isk-teal rounded-xl"
                  data-testid="select-property-type"
                >
                  <SelectValue placeholder="Pilih jenis properti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kost-existing">Kost Existing</SelectItem>
                  <SelectItem value="tanah-kosong">Tanah Kosong</SelectItem>
                  <SelectItem value="rumah-untuk-kost">Rumah untuk Kost</SelectItem>
                  <SelectItem value="apartemen">Apartemen</SelectItem>
                  <SelectItem value="lainnya">Lainnya</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-xl border border-isk-teal/10 bg-white/50">
              <button
                type="button"
                onClick={() => setShowOptionalFields((prev) => !prev)}
                className="w-full px-5 py-4 flex items-center justify-between text-left"
                data-testid="optional-fields-toggle"
              >
                <div>
                  <div className="font-semibold text-isk-text">Tambahkan detail opsional</div>
                  <div className="text-sm text-isk-muted">Email dan catatan singkat untuk mempercepat follow-up.</div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-isk-teal transition-transform ${showOptionalFields ? 'rotate-180' : ''}`}
                />
              </button>

              {showOptionalFields && (
                <div className="px-5 pb-5 space-y-6 border-t border-isk-teal/10">
                  <div className="pt-5 space-y-2">
                    <Label htmlFor="email" className="text-isk-text font-semibold">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      className="glass-input border-2 focus:border-isk-teal rounded-xl"
                      data-testid="input-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-isk-text font-semibold">
                      Detail Tambahan
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Contoh: lokasi properti, jumlah kamar, atau target kerjasama."
                      rows={4}
                      className="glass-input border-2 focus:border-isk-teal rounded-xl resize-none"
                      data-testid="input-message"
                    />
                  </div>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={status === 'loading'}
              className="w-full bg-isk-teal hover:bg-isk-dark text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-isk-teal/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {status === 'loading' ? 'Mengirim...' : 'Mulai Diskusi'}
            </Button>

            <p className="text-sm text-center text-isk-muted">
              Prioritas follow-up melalui WhatsApp. Email dan detail properti bisa ditambahkan bila diperlukan.
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
